import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { cx } from "../utils/cx";
import { isActiveLink, visibleNavigation } from "./navigation";

function NavLink({ item, pathname, onNavigate }) {
  const active = isActiveLink(item, pathname);
  return (
    <Link
      to={item.href}
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
      className={cx(
        "flex w-full items-center justify-between rounded-lg px-3 transition-all duration-300",
        active ? "bg-brand" : "hover:bg-brand/5"
      )}
    >
      <span
        className={cx(
          "flex min-w-0 gap-4 text-sm",
          item.description ? "items-start py-2.5" : "items-center py-3",
          active ? "font-bold text-white" : "text-slate-600"
        )}
      >
        <item.Icon className={cx("h-4 w-4 shrink-0", item.description && "mt-0.5")} />
        <span className="flex min-w-0 flex-col gap-0.5">
          {item.label}
          {item.description && (
            <span className={cx("text-xs font-normal leading-snug", active ? "text-white/75" : "text-slate-400")}>
              {item.description}
            </span>
          )}
        </span>
      </span>
      <span className={cx("h-5 w-[5px] rounded-md", active ? "bg-white" : "bg-transparent")} />
    </Link>
  );
}

const containsActive = (item, pathname) => item.children.some((child) => isActiveLink(child, pathname));

function NavGroup({ item, pathname, open, onToggle, onNavigate }) {
  const active = containsActive(item, pathname);

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between rounded-lg px-3 transition-all duration-300 hover:bg-brand/5"
      >
        <span className={cx("flex items-center gap-4 py-3 text-sm", active ? "font-medium text-brand" : "text-slate-600")}>
          <item.Icon className="h-4 w-4 shrink-0" />
          {item.label}
        </span>
        <ChevronDownIcon className={cx("h-3 w-3 text-slate-500 transition-transform duration-300 ease-in-out", open && "rotate-180")} />
      </button>
      {/* Height animates from 0fr to 1fr; a closed group stays mounted but inert, so its links cannot be focused */}
      <div
        inert={!open}
        className={cx(
          "grid transition-[grid-template-rows,opacity] duration-300 ease-in-out motion-reduce:transition-none",
          open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        )}
      >
        <div className="overflow-hidden">
          <div className="ml-4 mt-1 flex flex-col gap-1 border-l border-slate-200 pl-2">
            {item.children.map((child) => (
              <NavLink key={child.href} item={child} pathname={pathname} onNavigate={onNavigate} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Sidebar({ onNavigate, className }) {
  const { pathname } = useLocation();
  const items = visibleNavigation();
  // One group open at a time: the current page's group, until another is opened
  const activeGroupId = items.find((item) => item.children && containsActive(item, pathname))?.id ?? null;
  const [openId, setOpenId] = useState(activeGroupId);

  useEffect(() => {
    if (activeGroupId) setOpenId(activeGroupId);
  }, [activeGroupId]);

  return (
    <nav aria-label="Main" className={cx("thin-scrollbar flex flex-col gap-1 overflow-y-auto bg-white px-3 py-4 md:px-4", className)}>
      {items.map((item) =>
        item.children ? (
          <NavGroup
            key={item.id}
            item={item}
            pathname={pathname}
            open={openId === item.id}
            onToggle={() => setOpenId((current) => (current === item.id ? null : item.id))}
            onNavigate={onNavigate}
          />
        ) : (
          <NavLink key={item.id} item={item} pathname={pathname} onNavigate={onNavigate} />
        )
      )}
    </nav>
  );
}
